import { useSheet } from '@shared/hooks/use-sheet'
import { useState } from 'react'
import { useNavigate } from 'router-v6'
import { v4 as uuidv4 } from 'uuid'
import { ApplicationSheet } from 'v2/api-console/applications/application-sheet'
import type { ApplicationTableItem } from 'v2/api-console/applications/types'
import { REGIONS } from 'v2/configs/constants'
import { useBillingBalanceQuery, useConversionRate } from 'v2/pages/billing/queries'
import { FLOW_SECTIONS } from 'v2/pages/flow/flow-section'
import { classifyNumberConfig } from 'v2/pages/settings/phone-numbers/classify-number'
import { usePhoneNumbersQuery } from 'v2/pages/settings/phone-numbers/queries'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { useSandboxNumbersQuery } from 'v2/pages/settings/sandbox-numbers/queries'
import { SandboxConnectDialog } from 'v2/pages/settings/sandbox-numbers/sandbox-connect-dialog'
import {
  sandboxConnectCurrentId,
  sandboxConnectDialogKey,
  sandboxConnectKindFor,
} from 'v2/pages/settings/sandbox-numbers/sandbox-connect-payload'
import {
  resolveSignupNumber,
  showsComplianceComparison,
} from 'v2/pages/settings/sandbox-numbers/sandbox-gate'
import { UNLOCKED_CONCURRENCY } from 'v2/pages/settings/sandbox-numbers/sandbox-limits'
import {
  getConnectPermissions,
  SandboxConnectKind,
} from 'v2/pages/settings/sandbox-numbers/sandbox-setup-copy'
import { SandboxSetupPanel } from 'v2/pages/settings/sandbox-numbers/sandbox-setup-panel'
import type { SandboxTabAction } from 'v2/pages/settings/sandbox-numbers/sandbox-tab-action'
import { resolveTrialNumberRow } from 'v2/pages/settings/sandbox-numbers/sandbox-trial-number'
import { useSandboxSetupVisible } from 'v2/pages/settings/sandbox-numbers/use-sandbox-setup-visible'
import { useAuth } from 'v2/shared/hooks/use-auth'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import { useUserPlan } from 'v2/shared/hooks/use-plan'
import { formatCurrency } from 'v2/shared/utils'
import { agentWorkspace, canViewBuildSection } from 'v2/shared/utils/role-based-visibility'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { getLocalAuth } from 'v2/utils'

/**
 * Hosts the sandbox guided setup on Home.
 *
 * Gated identically to the Phone Numbers tab, through the same pure predicate,
 * so the two surfaces can never disagree about who is in sandbox.
 *
 * Manage/Add still deep-link to the Phone Numbers tab, where those dialogs and
 * their sheet state already live. Step 2 does NOT: it attaches something to the
 * sandbox number, which is this screen's own job, so it opens a picker here.
 */
export const SandboxSetupSection = () => {
  const navigate = useNavigate()
  const { isTrial } = useUserPlan()
  const { permissions } = usePermissions()
  const { data: profile } = useProfileQuery()
  const featureFlags = useFeatureFlags()
  const { details } = useAuth()
  const sessionRoles = details?.roles
  const canView = useSandboxSetupVisible()
  const [connectKind, setConnectKind] = useState<SandboxConnectKind>()
  // ApplicationSheet serves both create-an-application and create-a-trunk.
  const { openSheet } = useSheet<boolean | 'inbound' | 'outbound' | ApplicationTableItem>()

  const { data } = useSandboxNumbersQuery(canView)
  const { data: balance } = useBillingBalanceQuery()
  const { currencyCode } = useConversionRate()
  // Gated: this section mounts unconditionally on Home, and an account without
  // channelCalls view would otherwise 403 on every load. Two is already more
  // than the sandbox can have.
  const { data: numbersData } = usePhoneNumbersQuery({ limit: 2, offset: 0 }, canView)

  const trialRow = resolveTrialNumberRow(numbersData?.data?.numbers, isTrial)
  const appType = classifyNumberConfig(trialRow)
  const verified = data?.verified ?? []
  const signupNumber = resolveSignupNumber(data?.signup_number)

  // Connecting PATCHes the number's configuration, so it needs the call-channel
  // WRITE grant. The sandbox gate only asks for view.
  const canEditCalls = Boolean(permissions?.[RbacModules.channelCalls]?.[RbacPermissionTypes.edit])

  // Hooks above this line: `canView` is false on every non-sandbox account, and
  // an early return before them would break the hook order.
  if (!canView) return null

  // India-only: the compliance route does not exist elsewhere.
  const showsCompliance = showsComplianceComparison(getLocalAuth()?.region === REGIONS.IN)

  // Each kind's own RBAC, which the sandbox gate does not imply. An option the
  // role cannot act on is not offered rather than opening a picker whose create
  // link lands on a forbidden page.
  const workspace = agentWorkspace({
    permissionsMap: permissions,
    profileData: profile,
    featureFlags,
  })
  const allowed = getConnectPermissions(
    canViewBuildSection({ roles: sessionRoles, permissionsMap: permissions }),
    Boolean(workspace),
    canEditCalls,
  )
  const allowedConnectKinds = (Object.keys(allowed) as SandboxConnectKind[]).filter(
    (kind) => allowed[kind],
  )
  const connectCurrentId = connectKind ? sandboxConnectCurrentId(connectKind, trialRow) : undefined

  // Same derivation the Remaining Credits card uses, so the two agree.
  const creditBalance =
    balance?.amount != null
      ? formatCurrency(balance.amount, currencyCode, true, { minimumFractionDigits: 0 })
      : undefined

  /**
   * Carry the requested action across the navigation. Without it every guided
   * action lands on the same bare tab and the user has to find the thing they
   * just asked for.
   */
  const toSandboxTab = (action?: SandboxTabAction) =>
    navigate(
      '/phone-numbers/sandbox-numbers',
      action ? { state: { sandboxAction: action } } : undefined,
    )

  /**
   * Applications and trunks create in a side sheet; agents open the builder in a
   * new tab, because building an agent is a whole workspace rather than a form
   * and the design puts an external-link marker on that one.
   */
  const startCreate = (kind: SandboxConnectKind) => {
    if (kind === SandboxConnectKind.agent) {
      // Only offered when the org has somewhere to build (getConnectPermissions).
      if (workspace) {
        const { builderBase } = FLOW_SECTIONS[workspace]
        window.open(`${builderBase}/${uuidv4()}?new`, '_blank', 'noopener,noreferrer')
      }
      return
    }
    setConnectKind(undefined)
    openSheet(kind === SandboxConnectKind.sip ? 'inbound' : true)
  }

  return (
    <>
      <SandboxSetupPanel
        sandboxNumber={trialRow?.number}
        verified={verified}
        signupNumber={signupNumber}
        cap={data?.cap}
        unlockedConcurrency={UNLOCKED_CONCURRENCY}
        creditBalance={creditBalance}
        onManageNumbers={() => toSandboxTab('manage')}
        onAddNumber={() => toSandboxTab('add')}
        allowedConnectKinds={allowedConnectKinds}
        selectedConnectKind={sandboxConnectKindFor(appType)}
        onChooseConnect={setConnectKind}
        showsCompliance={showsCompliance}
        agentWorkspace={workspace}
        onStartKyc={() => navigate('/phone-numbers/compliance')}
        onCompare={() => toSandboxTab('compare')}
      />

      <SandboxConnectDialog
        // Both terms matter; see sandboxConnectDialogKey. Re-seeding can in
        // principle discard a pick made inside the sub-second window where the
        // number query settles, and seeding the value the number actually has
        // is the better of the two.
        key={sandboxConnectDialogKey(connectKind, connectCurrentId)}
        kind={connectKind}
        onClose={() => setConnectKind(undefined)}
        sandboxNumber={trialRow?.number}
        numberFields={trialRow}
        currentId={connectCurrentId}
        onCreate={startCreate}
        agentWorkspace={workspace}
      />

      {/*
        `openSheet` is a silent no-op unless a `#side-sheet` node is already in
        the DOM (`packages/shared/hooks/use-sheet.tsx`). SheetProvider is global
        but this marker is rendered per page by the table layouts, and Home uses
        none of them - so without this div both create links would do nothing at
        all, with no error.
      */}
      <div id="side-sheet">
        <ApplicationSheet />
      </div>
    </>
  )
}
